import React, { useState, useCallback, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import Select, { components } from "react-select";
import { DateRangePicker } from "rsuite";
import "rsuite/dist/rsuite.min.css";
import subDays from "date-fns/subDays";
import startOfWeek from "date-fns/startOfWeek";
import endOfWeek from "date-fns/endOfWeek";
import addDays from "date-fns/addDays";
import startOfMonth from "date-fns/startOfMonth";
import endOfMonth from "date-fns/endOfMonth";
import addMonths from "date-fns/addMonths";
import { toast, Toaster } from "react-hot-toast";
import { Search, RotateCcw, Download, X, MapPin, Eye, ChevronDown, ChevronUp } from "lucide-react";
import * as XLSX from "xlsx";
import { useCompanies, useTemplates } from "./hooks";
import { getIncorrectJsonNotes } from "./services/masterDataService";
import { useCurrentDomain } from "./context/DomainContext";
import JsonCompareModal from "./components/JsonCompareModal";
import { THEME } from "./theme/themeColors";


const BRAND       = THEME.primary;
const BRAND_LIGHT = THEME.primaryLight;
const BRAND_DARK  = THEME.primaryHover;
const ON_BRAND    = THEME.onPrimary;

const YES_BADGE     = { backgroundColor: "#dcfce7", color: "#166534" };
const NO_BADGE      = { backgroundColor: "#fde8e8", color: "#e02424" };
const UNKNOWN_BADGE = { backgroundColor: "#f1f5f9", color: "#475569" };

const customSelectStyles = {
    control: (base, state) => ({
        ...base, height: "32px", minHeight: "32px", fontSize: "13px",
        borderColor: state.isFocused ? BRAND : "#dee2e6",
        boxShadow: "none", borderRadius: "4px",
        "&:hover": { borderColor: BRAND },
    }),
    valueContainer: (base) => ({ ...base, padding: "0 8px", height: "32px", display: "flex", alignItems: "center" }),
    indicatorsContainer: (base) => ({ ...base, height: "32px" }),
    dropdownIndicator: (base) => ({ ...base, padding: "0 4px", color: "#6c757d" }),
    clearIndicator: (base) => ({ ...base, padding: "0 4px", color: "#6c757d" }),
    input: (base) => ({ ...base, margin: "0px" }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    option: (base, state) => ({
        ...base, fontSize: "12.5px",
        backgroundColor: state.isSelected ? BRAND : state.isFocused ? BRAND_LIGHT : "white",
        color: state.isSelected ? ON_BRAND : "#333", cursor: "pointer",
        borderRadius: "6px",
        padding: "9px 12px",
        margin: "2px 0",
        "&:active": { backgroundColor: BRAND_LIGHT },
    }),
};

const multiSelectStyles = {
    ...customSelectStyles,
    control: (base, state) => ({
        ...base,
        minHeight: "32px",
        height: "32px",
        fontSize: "13px",
        borderColor: state.isFocused ? BRAND : "#dee2e6",
        boxShadow: "none",
        borderRadius: "4px",
        "&:hover": { borderColor: BRAND },
    }),
    valueContainer: (base) => ({
        ...base,
        padding: "0 6px 0 8px",
        height: "32px",
        display: "flex",
        alignItems: "center",
        flexWrap: "nowrap",
        overflow: "hidden",
        gap: "4px",
    }),
    indicatorsContainer: (base) => ({ ...base, height: "32px" }),
    multiValue: (base) => ({
        ...base,
        backgroundColor: BRAND_LIGHT,
        border: `1px solid ${THEME.alpha(0.35)}`,
        borderRadius: "3px",
        margin: "0px",
        flexShrink: 0,
        maxWidth: "140px",
    }),
    multiValueLabel: (base) => ({
        ...base,
        color: BRAND_DARK,
        fontWeight: 600,
        fontSize: "11px",
        padding: "1px 5px",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    }),
    multiValueRemove: (base) => ({
        ...base,
        color: BRAND_DARK,
        cursor: "pointer",
        ":hover": { backgroundColor: BRAND, color: ON_BRAND },
    }),
    menu: (base) => ({
        ...base,
        marginTop: "6px",
        borderRadius: "6px",
        boxShadow: "0 6px 16px rgba(0,0,0,0.18)",
        border: "1px solid #d0d7de",
        overflow: "hidden",
        zIndex: 9999,
    }),
    menuList: (base) => ({
        ...base,
        padding: "4px",
        maxHeight: "240px",
    }),
};

const TooltipMultiValueLabel = (props) => (
    <span
        title={props.data?.label || ""}
        style={{ display: "inline-block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "100%" }}
    >
        <components.MultiValueLabel {...props} />
    </span>
);

const CustomValueContainer = ({ children, getValue, ...props }) => {
    const selected = getValue();
    const count = selected.length;

    if (count <= 2) {
        return <components.ValueContainer {...props}>{children}</components.ValueContainer>;
    }

    const childrenArray = React.Children.toArray(children);
    const inputComponent = childrenArray.find(
        (child) => child.props?.spec?.type === "input" || child.type === components.Input
    );
    const renderedTags = childrenArray
        .filter((child) => child.props?.index !== undefined)
        .slice(0, 2);

    const hiddenLabels = selected.slice(2).map((opt) => opt.label).join(", ");

    return (
        <components.ValueContainer {...props}>
            {renderedTags}
            <span
                className="badge rounded-pill fw-bold ms-1"
                title={hiddenLabels}
                style={{
                    backgroundColor: BRAND,
                    color: ON_BRAND,
                    fontSize: "10px",
                    padding: "3px 6px",
                    flexShrink: 0,
                    cursor: "default",
                }}
            >
                +{count - 2} more
            </span>
            {inputComponent}
        </components.ValueContainer>
    );
};

const predefinedBottomRanges = [
  { label: "Today", value: [new Date(), new Date()] },
  { label: "Yesterday", value: [addDays(new Date(), -1), addDays(new Date(), -1)] },
  { label: "This week", value: [startOfWeek(new Date()), endOfWeek(new Date())] },
  { label: "Last 7 days", value: [subDays(new Date(), 6), new Date()] },
  { label: "Last 30 days", value: [subDays(new Date(), 29), new Date()] },
  { label: "This month", value: [startOfMonth(new Date()), endOfMonth(new Date())] },
  { label: "Last month", value: [startOfMonth(addMonths(new Date(), -1)), endOfMonth(addMonths(new Date(), -1))] },
  { label: "This year", value: [new Date(new Date().getFullYear(), 0, 1), new Date(new Date().getFullYear(), 11, 31)] },
  { label: "Last year", value: [new Date(new Date().getFullYear() - 1, 0, 1), new Date(new Date().getFullYear() - 1, 11, 31)] },
];

const COLUMNS = [
    { label: "Case No",          key: "caseNo"                },
    { label: "Case ID",          key: "caseId"                },
    { label: "Event ID",         key: "eventID"               },
    { label: "Bill No",          key: "billNo"                },
    { label: "Billed Status",    key: "billedStatus"          },
    { label: "Finalized Status", key: "finalizedStatus"       },
    { label: "Event Date",       key: "eventDate"             },
    { label: "Patient Name",     key: "patientName"           },
    { label: "Doctor Name",      key: "doctorName"             },
    { label: "Doctor User",      key: "doctorUserName"         },
    { label: "Mismatch Count",   key: "mismatchCount"          },
    { label: "Locations",        key: "parentRenderKeyNames"   },
];

const normaliseBit = (value) => {
    if (value === null || value === undefined || value === "") return null;
    if (value === true  || value === 1  || value === "1"  || String(value).toLowerCase() === "true")  return true;
    if (value === false || value === 0  || value === "0"  || String(value).toLowerCase() === "false") return false;
    return null;
};

const getBitBadgeStyles = (value) => {
    const bit = normaliseBit(value);
    if (bit === true)  return YES_BADGE;
    if (bit === false) return NO_BADGE;
    return UNKNOWN_BADGE;
};

const getBilledLabel    = (v) => { const b = normaliseBit(v); return b === true ? "BILLED"    : b === false ? "NOT BILLED"    : "—"; };
const getFinalizedLabel = (v) => { const b = normaliseBit(v); return b === true ? "FINALIZED" : b === false ? "NOT FINALIZED" : "—"; };

const parseParentRenderKeyNames = (value) => {
    if (!value) return [];
    return String(value)
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
};

const EMPTY_PARAMS = { companyId: undefined, templateIds: undefined, fromDate: undefined, toDate: undefined };

const ParentKeysModal = ({ isOpen, onClose, row }) => {
    useEffect(() => {
        if (!isOpen) return;
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [isOpen, onClose]);

    if (!isOpen || !row) return null;

    const locations = parseParentRenderKeyNames(row.parentRenderKeyNames);

    const content = (
        <div
            className="pkm-backdrop"
            onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div className="pkm-dialog" role="dialog" aria-modal="true" aria-label="Mismatch Locations">
                <div className="pkm-header">
                    <div>
                        <div className="pkm-title">Mismatch Locations</div>
                        <div className="pkm-subtitle">
                            {row.caseNo ? `Case ${row.caseNo}` : ""}
                            {row.eventID ? ` · Event ${row.eventID}` : ""}
                            {" · "}{locations.length} location{locations.length !== 1 ? "s" : ""}
                        </div>
                    </div>
                    <button type="button" className="pkm-close" onClick={onClose} aria-label="Close">
                        <X size={16} />
                    </button>
                </div>
                <div className="pkm-body">
                    {locations.length === 0 ? (
                        <div className="pkm-empty">No locations recorded.</div>
                    ) : (
                        <ul className="pkm-list">
                            {locations.map((loc, i) => (
                                <li key={`${loc}-${i}`} className="pkm-list-item">
                                    <MapPin size={13} className="pkm-pin" />
                                    <span>{loc}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>

            <style>{`
                .pkm-backdrop {
                    position: fixed; inset: 0; background: rgba(15, 23, 42, 0.45);
                    display: flex; align-items: center; justify-content: center;
                    z-index: 2100; padding: 24px; animation: pkm-fade-in 0.12s ease;
                }
                .pkm-dialog {
                    background: #fff; border-radius: 10px; width: 100%; max-width: 420px;
                    max-height: 70vh; display: flex; flex-direction: column; overflow: hidden;
                    box-shadow: 0 20px 50px rgba(15, 23, 42, 0.25);
                }
                .pkm-header {
                    display: flex; align-items: flex-start; justify-content: space-between;
                    padding: 14px 16px; border-bottom: 1px solid #e9ecef; gap: 12px;
                }
                .pkm-title { font-size: 14px; font-weight: 700; color: #212529; }
                .pkm-subtitle { font-size: 11.5px; color: #6c757d; margin-top: 2px; }
                .pkm-close {
                    background: none; border: 1px solid #dee2e6; border-radius: 6px;
                    width: 26px; height: 26px; display: flex; align-items: center; justify-content: center;
                    color: #6c757d; cursor: pointer; flex-shrink: 0;
                }
                .pkm-close:hover { background: #f8f9fa; color: #212529; }
                .pkm-body { overflow: auto; padding: 8px; }
                .pkm-empty { padding: 24px 8px; text-align: center; color: #adb5bd; font-size: 12.5px; }
                .pkm-list { list-style: none; margin: 0; padding: 0; }
                .pkm-list-item {
                    display: flex; align-items: center; gap: 8px;
                    padding: 8px 10px; border-radius: 6px; font-size: 12.5px; color: #212529;
                }
                .pkm-list-item:hover { background: ${THEME.alpha(0.06)}; }
                .pkm-list-item + .pkm-list-item { border-top: 1px solid #f1f3f5; }
                .pkm-pin { color: ${BRAND_DARK}; flex-shrink: 0; }
                @keyframes pkm-fade-in { from { opacity: 0; } to { opacity: 1; } }
            `}</style>
        </div>
    );

    return ReactDOM.createPortal(content, document.body);
};

const IncorrectJsonNote = () => {
    const { domain } = useCurrentDomain();
    const { companies } = useCompanies(domain);
    const templates     = useTemplates(domain);

    const [rows, setRows]                           = useState([]);
    const [loading, setLoading]                     = useState(false);
    const [searchLoading, setSearchLoading]         = useState(false);

    const [colWidths, setColWidths] = useState([105, 135, 95, 105, 130, 145, 160, 190, 150, 150, 120, 130]);

    // Filters
    const [selectedCompany, setSelectedCompany]     = useState(null);
    const [selectedTemplates, setSelectedTemplates] = useState([]);
    const [fromDate, fromDateSet]                   = useState(null);
    const [toDate, toDateSet]                       = useState(null);
    const [jsonCompareRow, setJsonCompareRow]       = useState(null);
    const [parentKeysRow, setParentKeysRow]         = useState(null);

    const activeIndex = useRef(null);
    const startX      = useRef(0);
    const startWidth  = useRef(0);
    const [showFilters, setShowFilters] = useState(true);

    const [isMobile, setIsMobile] = useState(false);

    const fetchSeqRef = useRef(0);

    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth < 768);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        fetchSeqRef.current++;
        setSelectedCompany(null);
        setSelectedTemplates([]);
        setRows([]);
        setSearchLoading(false);
        setLoading(false);
    }, [domain]);

    const handleMouseMove = useCallback((e) => {
        if (activeIndex.current === null) return;
        const computed = Math.max(60, startWidth.current + (e.clientX - startX.current));
        setColWidths((prev) => {
            const updated = [...prev];
            updated[activeIndex.current] = computed;
            return updated;
        });
    }, []);

    const handleMouseUp = useCallback(() => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        activeIndex.current = null;
        document.body.style.userSelect = "";
    }, [handleMouseMove]);

    const handleMouseDown = (index, e) => {
        activeIndex.current = index;
        startX.current      = e.clientX;
        startWidth.current  = colWidths[index];
        document.body.style.userSelect = "none";
        document.addEventListener("mousemove", handleMouseMove);
        document.addEventListener("mouseup", handleMouseUp);
    };

    useEffect(() => () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
    }, [handleMouseMove, handleMouseUp]);

    const buildParams = useCallback(() => {
        let adjustedToDate = toDate;
        if (toDate) {
            adjustedToDate = new Date(toDate);
            adjustedToDate.setHours(23, 59, 59, 999);
        }
        return {
            companyId:   selectedCompany?.value                                    || undefined,
            templateIds: selectedTemplates.length > 0
                ? selectedTemplates.map(t => t.value)
                : undefined,
            fromDate:    fromDate         ? fromDate.toISOString()                 : undefined,
            toDate:      adjustedToDate   ? adjustedToDate.toISOString()           : undefined,
        };
    }, [selectedCompany, selectedTemplates, fromDate, toDate]);

    const fetchRows = useCallback(async (isNewSearch, paramsOverride) => {
        const seq = ++fetchSeqRef.current;
        const setLoader = isNewSearch ? setSearchLoading : setLoading;
        setLoader(true);
        try {
            const res = await getIncorrectJsonNotes(domain, paramsOverride || buildParams());
            if (seq !== fetchSeqRef.current) return;
            setRows(res?.data?.results || res?.data || []);
        } catch (err) {
            if (seq !== fetchSeqRef.current) return;
            toast.error(err.response?.data?.message || "Failed to load records");
            console.error(err);
        } finally {
            if (seq === fetchSeqRef.current) setLoader(false);
        }
    }, [domain, buildParams]);

    const handleSearch = () => fetchRows(true);

    const handleReset = () => {
        setSelectedCompany(null);
        setSelectedTemplates([]);
        fromDateSet(null);
        toDateSet(null);
        setRows([]);
        fetchRows(true, EMPTY_PARAMS);
    };

    const exportAllToExcel = () => {
        if (!rows.length) { toast.error("No data to export"); return; }
        const tid = toast.loading("Preparing export...");
        try {
            const exportData = rows.map(row => ({
                CASE_NO:            row.caseNo             || "",
                CASE_ID:            row.caseId             || "",
                EVENT_ID:           row.eventID            || "",
                BILL_NO:            row.billNo             || "",
                BILLED_STATUS:      getBilledLabel(row.billedStatus),
                FINALIZED_STATUS:   getFinalizedLabel(row.finalizedStatus),
                EVENT_DATE:         row.eventDate ? new Date(row.eventDate).toLocaleString() : "",
                PATIENT_NAME:       row.patientName        || "",
                DOCTOR_NAME:        row.doctorName         || "",
                DOCTOR_USER_NAME:   row.doctorUserName     || "",
                MISMATCH_COUNT:     row.mismatchCount ?? "",
                LOCATIONS:          row.parentRenderKeyNames || "",
            }));
            const ws = XLSX.utils.json_to_sheet(exportData);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "IncorrectJsonNotes");
            XLSX.writeFile(wb, `IncorrectJsonNotes_${new Date().toISOString().slice(0, 10)}.xlsx`);
            toast.success(`Exported ${exportData.length} record${exportData.length !== 1 ? "s" : ""}`);
        } catch {
            toast.error("Failed to export");
        } finally {
            toast.dismiss(tid);
        }
    };

    const tdStyle = (i) => ({
        width: `${colWidths[i]}px`,
        minWidth: `${colWidths[i]}px`,
        maxWidth: `${colWidths[i]}px`
    });

    const filterLabelStyle = {
        fontSize: "10px", fontWeight: 600, color: "#6c757d",
        textTransform: "uppercase", letterSpacing: "0.3px",
        marginBottom: "3px", display: "block",
    };

    const isBusy = loading || searchLoading;

    const iconBtn = (extraPad) => ({
        height: "32px",
        width: isMobile ? "48px" : "auto",
        padding: isMobile ? "0" : extraPad,
    });

    return (
        <div className="bg-light min-vh-100">
            <Toaster position="top-right" />

            <div className="card shadow-sm border-0 rounded-3 bg-white">

                {/* Filter bar */}
                <div className="border-bottom px-2 py-2" style={{ backgroundColor: "#fcfcfc" }}>
                {showFilters && (
                    <div className="d-flex gap-2 align-items-end flex-wrap mb-2">

                    {/* Company */}
                    <div style={{ flex: isMobile ? "1 1 100%" : "1 1 200px", minWidth: isMobile ? "100%" : "190px" }}>
                        <label style={filterLabelStyle} htmlFor="ijn-company">Company</label>
                        <Select
                            inputId="ijn-company"
                            options={companies}
                            value={selectedCompany}
                            onChange={setSelectedCompany}
                            placeholder="Select company..."
                            isSearchable isClearable
                            menuPortalTarget={document.body}
                            styles={customSelectStyles}
                        />
                    </div>

                    {/* Template */}
                    <div style={{ flex: isMobile ? "1 1 100%" : "1 1 240px", minWidth: isMobile ? "100%" : "220px" }}>
                        <label style={filterLabelStyle} htmlFor="ijn-template">Template</label>
                        <Select
                            inputId="ijn-template"
                            options={templates}
                            value={selectedTemplates}
                            onChange={(val) => setSelectedTemplates(val || [])}
                            placeholder="Select template(s)..."
                            isSearchable isClearable isMulti
                            menuPortalTarget={document.body}
                            styles={multiSelectStyles}
                            classNamePrefix="ijn-template-select"
                            components={{
                                ValueContainer: CustomValueContainer,
                                MultiValueLabel: TooltipMultiValueLabel,
                            }}
                            closeMenuOnSelect={false}
                            hideSelectedOptions={false}
                        />
                    </div>

                    {/* Date Range */}
                    <div style={{ flex: isMobile ? "1 1 100%" : "1 1 200px", minWidth: isMobile ? "100%" : "190px" }}>
                    <label style={filterLabelStyle}>Date Range</label>
                    <DateRangePicker
                        value={[fromDate, toDate]}
                        onChange={(value) => {
                            const [start, end] = value || [];
                            fromDateSet(start || null);
                            toDateSet(end || null);
                        }}
                        ranges={predefinedBottomRanges}
                        placeholder="Select Date Range"
                        style={{ width: '100%' }}
                        format="MM/dd/yyyy"
                        size="sm"
                        cleanable
                        placement="bottomStart"
                        preventOverflow
                        showOneCalendar={isMobile}
                    />
                    </div>

                    <div className="d-flex gap-2 align-items-end flex-wrap ms-auto">
                        <button
                            className="btn btn-theme fw-medium d-flex align-items-center justify-content-center"
                            style={{ ...iconBtn("0 16px"), fontSize: "12px" }}
                            onClick={handleSearch}
                            disabled={isBusy}
                            title="Search"
                            aria-label="Search"
                        >
                            {searchLoading
                                ? <span className="spinner-border spinner-border-sm" role="status" />
                                : isMobile ? <Search size={18} /> : "Search"}
                        </button>

                        <button
                            className="btn btn-sm btn-outline-secondary d-flex align-items-center justify-content-center"
                            style={iconBtn("0 12px")}
                            onClick={handleReset}
                            title="Reset"
                            aria-label="Reset"
                        >
                            <RotateCcw size={18} />
                        </button>

                        <button
                            className="btn btn-sm btn-outline-secondary d-flex align-items-center justify-content-center"
                            style={iconBtn("0 12px")}
                            onClick={exportAllToExcel}
                            title="Export To Excel"
                            aria-label="Export to Excel"
                        >
                            <Download size={18} />
                        </button>

                        {/* Hide Filters - Right side */}
                        <button
                            className="btn btn-sm btn-outline-secondary d-md-none d-flex align-items-center gap-1 ms-auto"
                            style={{ height: "32px", fontSize: "12px" }}
                            onClick={() => setShowFilters(false)}
                        >
                            Hide Filters <ChevronDown size={14} />
                        </button>
                    </div>
                    </div>
                )}

                {/* Show Filters Header */}
                <div className="d-flex justify-content-end align-items-center mt-2 pt-2 border-top">
                    {!showFilters && (
                    <button
                        className="btn btn-sm btn-outline-secondary d-md-none d-flex align-items-center gap-1"
                        style={{ fontSize: '12px', height: '28px' }}
                        onClick={() => setShowFilters(true)}
                    >
                        Show Filters <ChevronUp size={14} />
                    </button>
                    )}
                </div>
                </div>

                <div className="table-responsive scroll-hidden" style={{ maxHeight: "70vh", overflowY: "auto" }}>
                    <table
                        className="table table-hover align-middle mb-0 d-none d-lg-table ijn-table"
                        style={{
                            tableLayout: "fixed",
                            fontSize: "12.5px",
                            width: `${colWidths.reduce((a, b) => a + b, 0)}px`,
                            minWidth: "100%"
                        }}
                    >
                        <thead className="table-light sticky-top border-bottom" style={{ zIndex: 10 }}>
                            <tr>
                                {COLUMNS.map((col, index) => (
                                    <th
                                        key={col.key}
                                        className="text-secondary fw-bold text-uppercase py-2 px-2 position-relative"
                                        style={{
                                            width: `${colWidths[index]}px`,
                                            minWidth: `${colWidths[index]}px`,
                                            maxWidth: `${colWidths[index]}px`,
                                            fontSize: "10.5px",
                                            letterSpacing: "0.4px",
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                        }}
                                    >
                                        <span>{col.label}</span>
                                        <div
                                            onMouseDown={(e) => handleMouseDown(index, e)}
                                            style={{ position: "absolute", right: 0, top: 0, bottom: 0, width: "6px", cursor: "col-resize", zIndex: 11 }}
                                            className="column-resizer-handle"
                                            aria-hidden="true"
                                        />
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {isBusy && rows.length === 0 ? (
                                <tr>
                                    <td colSpan={COLUMNS.length} className="text-center py-4 border-0">
                                        <span className="spinner-border spinner-border-sm" style={{ color: BRAND }} role="status" />
                                        <span className="ms-2 text-muted">Loading records...</span>
                                    </td>
                                </tr>
                            ) : rows.length === 0 ? (
                                <tr>
                                    <td colSpan={COLUMNS.length} className="text-center py-4 text-muted border-0">
                                        No matching records found.
                                    </td>
                                </tr>
                            ) : (
                                rows.map((row, idx) => {
                                    const bBadge = getBitBadgeStyles(row.billedStatus);
                                    const fBadge = getBitBadgeStyles(row.finalizedStatus);
                                    const locations = parseParentRenderKeyNames(row.parentRenderKeyNames);
                                    return (
                                        <tr key={`${row.caseId || idx}-${row.eventID || idx}`} style={{ height: "30px" }}>

                                            {/* 0 — Case No */}
                                            <td className="py-1 px-2 text-truncate text-secondary" title={row.caseNo} style={tdStyle(0)}>
                                                {row.caseNo || "—"}
                                            </td>

                                            {/* 1 — Case ID */}
                                            <td className="py-1 px-2 text-truncate fw-medium text-dark" title={row.caseId} style={tdStyle(1)}>
                                                {row.caseId || "—"}
                                            </td>

                                            {/* 2 — Event ID — clickable, opens the JSON compare popup */}
                                            <td className="py-1 px-2 text-truncate" style={tdStyle(2)}>
                                                {row.eventID ? (
                                                    <button
                                                        type="button"
                                                        className="btn btn-link p-0 fw-medium text-truncate ijn-link"
                                                        style={{ fontSize: "12.5px", maxWidth: "100%" }}
                                                        onClick={() => setJsonCompareRow(row)}
                                                        title={`View JSON comparison for event ${row.eventID}`}
                                                    >
                                                        {row.eventID}
                                                    </button>
                                                ) : "—"}
                                            </td>

                                            {/* 3 — Bill No */}
                                            <td className="py-1 px-2 text-truncate text-secondary" title={row.billNo} style={tdStyle(3)}>
                                                {row.billNo || "—"}
                                            </td>

                                            {/* 4 — Billed Status */}
                                            <td className="py-1 px-2 text-center text-truncate" style={tdStyle(4)}>
                                                {normaliseBit(row.billedStatus) !== null ? (
                                                    <span className="badge fw-bold px-15 py-05"
                                                        style={{ ...bBadge, borderRadius: "3px", fontSize: "10px" }}>
                                                        {getBilledLabel(row.billedStatus)}
                                                    </span>
                                                ) : "—"}
                                            </td>

                                            {/* 5 — Finalized Status */}
                                            <td className="py-1 px-2 text-center text-truncate" style={tdStyle(5)}>
                                                {normaliseBit(row.finalizedStatus) !== null ? (
                                                    <span className="badge fw-bold px-15 py-05"
                                                        style={{ ...fBadge, borderRadius: "3px", fontSize: "10px" }}>
                                                        {getFinalizedLabel(row.finalizedStatus)}
                                                    </span>
                                                ) : "—"}
                                            </td>

                                            {/* 6 — Event Date */}
                                            <td className="py-1 px-2 text-truncate text-secondary" title={row.eventDate ? new Date(row.eventDate).toLocaleString() : ""} style={tdStyle(6)}>
                                                {row.eventDate ? new Date(row.eventDate).toLocaleDateString() : "—"}
                                            </td>

                                            {/* 7 — Patient Name */}
                                            <td className="py-1 px-2 text-truncate fw-medium text-dark" title={row.patientName} style={tdStyle(7)}>
                                                {row.patientName || "—"}
                                            </td>

                                            {/* 8 — Doctor Name */}
                                            <td className="py-1 px-2 text-truncate text-secondary" title={row.doctorName} style={tdStyle(8)}>
                                                {row.doctorName || "—"}
                                            </td>

                                            {/* 9 — Doctor User Name */}
                                            <td className="py-1 px-2 text-truncate text-secondary" title={row.doctorUserName} style={tdStyle(9)}>
                                                {row.doctorUserName || "—"}
                                            </td>

                                            {/* 10 — Mismatch Count */}
                                            <td className="py-1 px-2 text-center text-truncate" style={tdStyle(10)}>
                                                {row.mismatchCount !== null && row.mismatchCount !== undefined ? (
                                                    <span
                                                    className="badge fw-bold px-15 py-05"
                                                    style={{ backgroundColor: "#fdf3d8", color: "#a86c00", borderRadius: "3px", fontSize: "10px" }}
                                                    >
                                                        {row.mismatchCount}
                                                    </span>
                                                ) : "—"}
                                            </td>

                                            <td className="py-1 px-2 text-truncate" style={tdStyle(11)}>
                                                {locations.length > 0 ? (
                                                    <button
                                                        type="button"
                                                        className="btn btn-link p-0 fw-medium text-truncate d-inline-flex align-items-center gap-1 ijn-link"
                                                        style={{ fontSize: "12.5px", maxWidth: "100%" }}
                                                        onClick={() => setParentKeysRow(row)}
                                                        title={row.parentRenderKeyNames}
                                                    >
                                                        <MapPin size={12} />
                                                        {locations.length} location{locations.length !== 1 ? "s" : ""}
                                                    </button>
                                                ) : "—"}
                                            </td>

                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>

                    {/* CARD VIEW */}
                    <div className="d-lg-none" style={{ padding: '0 8px' }}>
                        {isBusy ? (
                            <div className="text-center py-5">
                                <span className="spinner-border spinner-border-sm" style={{ color: BRAND }} role="status" />
                                <div className="mt-2 text-muted">Loading records...</div>
                            </div>
                        ) : rows.length === 0 ? (
                            <div className="text-center py-5 text-muted">No matching records found.</div>
                        ) : (
                            rows.map((row, idx) => {
                                const bBadge = getBitBadgeStyles(row.billedStatus);
                                const fBadge = getBitBadgeStyles(row.finalizedStatus);
                                const locations = parseParentRenderKeyNames(row.parentRenderKeyNames);
                                return (
                                    <div key={`${row.caseId || idx}-${row.eventID || idx}`} style={{
                                        background: '#fff',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '10px',
                                        padding: '14px',
                                        marginBottom: '12px',
                                        boxShadow: '0 1px 4px rgba(0,0,0,0.06)'
                                    }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                                            <div>
                                                <div style={{ fontSize: '13px', color: '#64748b' }}>CASE NO</div>
                                                <div style={{ fontSize: '14.5px', fontWeight: 500 }}>{row.caseNo || "—"}</div>
                                            </div>
                                            <div>
                                                {row.eventID && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm p-1"
                                                        style={{ color: BRAND_DARK }}
                                                        onClick={() => setJsonCompareRow(row)}
                                                        aria-label={`View JSON comparison for event ${row.eventID}`}
                                                    >
                                                        <Eye size={16} />
                                                    </button>
                                                )}
                                            </div>
                                        </div>

                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '10px' }}>
                                            <div>
                                                <div style={{ fontSize: '11px', color: '#6c757d', fontWeight: 600 }}>CASE ID</div>
                                                <div style={{ fontSize: '13.5px', fontWeight: 500 }}>{row.caseId || "—"}</div>
                                            </div>
                                            <div>
                                                <div style={{ fontSize: '11px', color: '#6c757d', fontWeight: 600 }}>EVENT ID</div>
                                                <div style={{ fontSize: '13.5px', fontWeight: 500 }}>{row.eventID || "—"}</div>
                                            </div>
                                        </div>

                                        <div style={{ fontSize: '13px', marginBottom: '8px' }}>
                                            <strong>BILL NO:</strong> {row.billNo || "—"}
                                        </div>

                                        <div style={{ display: 'flex', gap: '16px', marginBottom: '10px' }}>
                                            <div>
                                                <div style={{ fontSize: '11px', color: '#6c757d', fontWeight: 600 }}>BILLED</div>
                                                <span className="badge fw-bold px-15 py-05" style={{ ...bBadge, borderRadius: "3px", fontSize: "10px" }}>
                                                    {getBilledLabel(row.billedStatus)}
                                                </span>
                                            </div>
                                            <div>
                                                <div style={{ fontSize: '11px', color: '#6c757d', fontWeight: 600 }}>FINALIZED</div>
                                                <span className="badge fw-bold px-15 py-05" style={{ ...fBadge, borderRadius: "3px", fontSize: "10px" }}>
                                                    {getFinalizedLabel(row.finalizedStatus)}
                                                </span>
                                            </div>
                                        </div>

                                        <div style={{ fontSize: '13px', marginBottom: '8px' }}>
                                            <strong>EVENT DATE:</strong> {row.eventDate ? new Date(row.eventDate).toLocaleDateString() : "—"}
                                        </div>

                                        <div style={{ fontSize: '13px', marginBottom: '8px' }}>
                                            <strong>PATIENT NAME:</strong> {row.patientName || "—"}
                                        </div>

                                        <div style={{ fontSize: '13px', marginBottom: '8px' }}>
                                            <strong>DOCTOR NAME:</strong> {row.doctorName || "—"}
                                        </div>

                                        <div style={{ fontSize: '13px', marginBottom: '8px' }}>
                                            <strong>DOCTOR USER:</strong> {row.doctorUserName || "—"}
                                        </div>

                                        <div style={{ fontSize: '13px', marginBottom: '8px' }}>
                                            <strong>MISMATCH COUNT:</strong> {row.mismatchCount ?? "—"}
                                        </div>

                                        {locations.length > 0 && (
                                            <button
                                                type="button"
                                                className="btn btn-link p-0 fw-medium text-truncate d-inline-flex align-items-center gap-1 mt-2 ijn-link"
                                                style={{ fontSize: "12.5px" }}
                                                onClick={() => setParentKeysRow(row)}
                                            >
                                                <MapPin size={13} />
                                                LOCATIONS ({locations.length})
                                            </button>
                                        )}
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>

                {/* ── Footer (matches MixPanelLogs exactly) ─────────────────── */}
                <div className="px-2 py-1 border-top d-flex align-items-center justify-content-between"
                    style={{ backgroundColor: "#fafafa" }}>
                    <span className="text-muted" style={{ fontSize: "12px" }}>
                        Showing <strong>{rows.length}</strong> record{rows.length !== 1 ? "s" : ""}
                    </span>
                </div>
            </div>

            <JsonCompareModal
                isOpen={!!jsonCompareRow}
                onClose={() => setJsonCompareRow(null)}
                domain={domain}
                row={jsonCompareRow}
            />

            <ParentKeysModal
                isOpen={!!parentKeysRow}
                onClose={() => setParentKeysRow(null)}
                row={parentKeysRow}
            />

            <style>{`
                @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
                .ijn-table { --bs-table-hover-bg: ${THEME.alpha(0.05)}; }
                .ijn-link { color: ${BRAND_DARK} !important; text-decoration: none; }
                .ijn-link:hover, .ijn-link:focus-visible { text-decoration: underline; }
                .column-resizer-handle { transition: background-color 0.15s ease; }
                .column-resizer-handle:hover,
                .column-resizer-handle:active { background-color: ${BRAND} !important; width: 4px !important; }
                .py-05 { padding-top: 0.125rem; padding-bottom: 0.125rem; }
                .px-15 { padding-left: 0.375rem; padding-right: 0.375rem; }
                .scroll-hidden { scrollbar-width: none; -ms-overflow-style: none; }
                .scroll-hidden::-webkit-scrollbar { display: none; width: 0; height: 0; }

                /* Safety-net rules for the Template multi-select dropdown menu.
                   These use classNamePrefix + !important so they take effect
                   even if react-select's styles-prop merging gets overridden
                   by global Bootstrap/reset CSS elsewhere in the app. */
                .ijn-template-select__menu-portal {
                    z-index: 99999 !important;
                }
                .ijn-template-select__menu {
                    margin-top: 8px !important;
                    border-radius: 8px !important;
                    box-shadow: 0 10px 24px rgba(15, 23, 42, 0.12), 0 2px 6px rgba(15, 23, 42, 0.08) !important;
                    border: 1px solid #e5e9ee !important;
                    overflow: hidden !important;
                    padding: 0 !important;
                }
                .ijn-template-select__menu-list {
                    padding: 6px !important;
                    max-height: 260px !important;
                    scrollbar-width: thin !important;
                    scrollbar-color: #c8d3dc transparent !important;
                }
                .ijn-template-select__menu-list::-webkit-scrollbar {
                    width: 6px !important;
                }
                .ijn-template-select__menu-list::-webkit-scrollbar-track {
                    background: transparent !important;
                }
                .ijn-template-select__menu-list::-webkit-scrollbar-thumb {
                    background-color: #c8d3dc !important;
                    border-radius: 10px !important;
                }
                .ijn-template-select__option {
                    border-radius: 6px !important;
                    padding: 9px 12px !important;
                    margin: 2px 0 !important;
                    font-size: 12.5px !important;
                    transition: background-color 0.12s ease !important;
                }
                .ijn-template-select__multi-value {
                    background-color: ${BRAND_LIGHT} !important;
                    border: 1px solid ${THEME.alpha(0.35)} !important;
                    border-radius: 3px !important;
                    margin: 0 !important;
                    flex-shrink: 0 !important;
                    max-width: 140px !important;
                    overflow: hidden !important;
                    display: flex !important;
                    align-items: center !important;
                }
                .ijn-template-select__multi-value__label {
                    color: ${BRAND_DARK} !important;
                    font-weight: 600 !important;
                    font-size: 11px !important;
                    padding: 1px 5px !important;
                    overflow: hidden !important;
                    text-overflow: ellipsis !important;
                    white-space: nowrap !important;
                    max-width: 110px !important;
                    display: inline-block !important;
                }
                .ijn-template-select__multi-value__remove {
                    color: ${BRAND_DARK} !important;
                    cursor: pointer !important;
                    flex-shrink: 0 !important;
                }
                .ijn-template-select__multi-value__remove:hover {
                    background-color: ${BRAND} !important;
                    color: ${ON_BRAND} !important;
                }
                @media (max-width: 768px) {
                    .rsuite .rs-picker-date-range { width: 100% !important; }
                    .rs-picker-date-range .rs-picker-toggle { width: 100% !important; padding: 8px 12px !important; font-size: 13px !important; }
                    .rs-picker-date-range .rs-picker-menu { width: 100% !important; max-width: 100vw !important; left: 0 !important; right: 0 !important; margin: 4px 0 !important; }
                    .rs-picker-date-range .rs-picker-daterange-calendar-group > div:last-child { display: none !important; }
                }
            `}</style>
        </div>
    );
};

export default IncorrectJsonNote;